import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { productsAPI } from '../../api/products';
import { Category } from '../../types';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const CategoriesAdminPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
    is_active: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const res = await productsAPI.getCategories();
      if (res.success && res.data) setCategories(res.data);
    } catch {
      toast.error('Failed to fetch categories');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (name: string) =>
    name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await productsAPI.updateCategory(editingId, formData);
        toast.success('Category updated');
      } else {
        await productsAPI.createCategory(formData);
        toast.success('Category created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', slug: '', description: '', image_url: '', is_active: true });
      fetchCategories();
    } catch {
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (cat: Category) => {
    setEditingId(cat.id);
    setFormData({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      image_url: cat.image_url || '',
      is_active: cat.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await productsAPI.deleteCategory(id);
      toast.success('Category deleted');
      fetchCategories();
    } catch {
      toast.error('Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">
            Management
          </p>
          <h1 className="font-serif text-3xl text-primary">Categories</h1>
        </div>
        <button
          onClick={() => {
            setEditingId(null);
            setFormData({ name: '', slug: '', description: '', image_url: '', is_active: true });
            setShowForm(!showForm);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>Add Category</span>
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-light p-6"
        >
          <h2 className="font-serif text-xl text-primary mb-6">
            {editingId ? 'Edit Category' : 'New Category'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Name
              </label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: generateSlug(e.target.value),
                })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Slug
              </label>
              <input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Image URL
              </label>
              <input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="input-luxury"
                placeholder="https://..."
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full border border-light p-3 text-xs outline-none focus:border-primary transition-colors resize-none"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="accent-primary"
              />
              <span className="text-xs tracking-widest uppercase">Active</span>
            </div>
            <div className="col-span-2 flex space-x-3">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-32 rounded" />
          ))
        ) : categories.length === 0 ? (
          <div className="col-span-3 text-center py-16 bg-white border border-light">
            <p className="text-xs tracking-widest uppercase text-nude">
              No categories found
            </p>
          </div>
        ) : (
          categories.map((cat) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-light overflow-hidden"
            >
              {cat.image_url && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif text-lg text-primary">{cat.name}</h3>
                  <span className={`text-xs tracking-widest uppercase px-2 py-0.5 rounded ${
                    cat.is_active
                      ? 'bg-green-50 text-green-600'
                      : 'bg-red-50 text-red-500'
                  }`}>
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-xs text-nude mb-1">/{cat.slug}</p>
                {cat.description && (
                  <p className="text-xs text-secondary line-clamp-2">
                    {cat.description}
                  </p>
                )}
                <div className="flex items-center space-x-3 mt-4">
                  <button
                    onClick={() => handleEdit(cat)}
                    className="flex items-center space-x-1 text-xs text-nude hover:text-primary transition-colors"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="flex items-center space-x-1 text-xs text-nude hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={12} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoriesAdminPage;